/**
 * Datto SaaS Provider Detection Utilities
 *
 * Client-side heuristic matching to identify cloud providers from asset names.
 * Focus on Microsoft 365 / Azure (primary requirement).
 */

/**
 * Identify SaaS provider from asset name/metadata
 * Focus on M365/Azure as primary requirement
 *
 * @param {object} asset - Datto asset object with name, os, type fields
 * @returns {object} Provider info with provider name, icon, and color
 */
export const identifySaasProvider = (asset) => {
  // A SaaS Protection record names its product (`productType`, mirrored onto the asset
  // as `provider`). Trust that before guessing from the customer's company name - a
  // tenant called "Unisan Ltd" is still Microsoft 365.
  const product = String(asset.productType || asset.provider || '').toLowerCase().replace(/[\s_-]/g, '');
  if (product) {
    if (/(office365|microsoft365|m365|o365|exchange|sharepoint|onedrive)/.test(product)) {
      return { provider: 'M365', icon: 'CloudOutlined', color: 'blue' };
    }
    if (/(google|gsuite|workspace)/.test(product)) {
      return { provider: 'Google Workspace', icon: 'GoogleOutlined', color: 'var(--odin-status-completed-text)' };
    }
  }

  const name = (asset.name || '').toLowerCase();
  const os = (asset.os || '').toLowerCase();

  // Microsoft 365 detection
  if (
    name.includes('microsoft') ||
    name.includes('m365') ||
    name.includes('office365') ||
    name.includes('office 365') ||
    name.includes('onmicrosoft.com') ||
    name.includes('sharepoint') ||
    name.includes('exchange') ||
    name.includes('teams') ||
    name.includes('onedrive')
  ) {
    return { provider: 'M365', icon: 'CloudOutlined', color: 'blue' };
  }

  // Azure detection
  if (name.includes('azure') || os.includes('azure')) {
    return { provider: 'Azure', icon: 'CloudServerOutlined', color: 'geekblue' };
  }

  // AWS detection (secondary)
  if (name.includes('aws') || name.includes('amazon')) {
    return { provider: 'AWS', icon: 'CloudOutlined', color: 'var(--odin-status-awaiting-text)' };
  }

  // Google Workspace (secondary)
  if (
    name.includes('google') ||
    name.includes('gsuite') ||
    name.includes('g suite') ||
    name.includes('workspace')
  ) {
    return { provider: 'Google Workspace', icon: 'GoogleOutlined', color: 'var(--odin-status-completed-text)' };
  }

  // Salesforce (secondary)
  if (name.includes('salesforce')) {
    return { provider: 'Salesforce', icon: 'CloudOutlined', color: 'var(--odin-status-active-text)' };
  }

  // Generic SaaS fallback
  return { provider: 'Other SaaS', icon: 'CloudOutlined', color: 'var(--odin-text-secondary)' };
};

/**
 * Convert one Datto SaaS Protection domain record (Datto-MCP `list_saas_domains`) into
 * the asset shape the Datto page's SaaS card and table render.
 *
 * The SaaS record has no backup timestamp - only service counts and a percentage - so
 * the asset deliberately carries no `lastBackup`. Status comes from the counts:
 * every service recently backed up -> protected; some -> warning; none -> unprotected.
 * `protectionEnabled` means protection is configured at all (any active service).
 *
 * @param {object} domain - Raw domain record from list_saas_domains
 * @returns {object} SaaS asset
 */
export const saasDomainToAsset = (domain) => {
  const stats = domain.backupStats || {};
  const servicesTotal = stats.activeServicesCount || 0;
  const servicesProtected = stats.activeServicesWithRecentBackupCount || 0;
  const productType = domain.productType || 'Office365';

  let status = 'unprotected';
  if (servicesTotal > 0 && servicesProtected >= servicesTotal) status = 'protected';
  else if (servicesProtected > 0) status = 'warning';

  return {
    name: domain.saasCustomerName || domain.domain,
    type: 'SaaS',
    provider: productType,
    productType,
    domain: domain.domain,
    saasCustomerId: domain.saasCustomerId,
    seatsUsed: domain.seatsUsed || 0,
    servicesTotal,
    servicesProtected,
    backupPercentage: stats.backupPercentage || 0,
    protectionEnabled: servicesTotal > 0,
    status,
  };
};

/**
 * Human-readable coverage line for SaaS assets that have service counts but no
 * timestamp, e.g. "34 of 35 services backed up recently". Null when no asset has counts.
 *
 * @param {Array} assets
 * @returns {string|null}
 */
export const describeSaasCoverage = (assets) => {
  const withCounts = (assets || []).filter(a => typeof a.servicesTotal === 'number');
  if (withCounts.length === 0) return null;
  const total = withCounts.reduce((n, a) => n + (a.servicesTotal || 0), 0);
  const recent = withCounts.reduce((n, a) => n + (a.servicesProtected || 0), 0);
  return `${recent} of ${total} services backed up recently`;
};

/**
 * Categorize SaaS assets by provider
 * Returns a map of provider name to array of assets
 *
 * @param {Array} assets - Array of all Datto assets
 * @returns {object} Map of provider name to array of SaaS assets
 */
export const categorizeSaasAssets = (assets) => {
  const saasAssets = assets.filter(a => a.type?.toLowerCase() === 'saas');

  return saasAssets.reduce((acc, asset) => {
    const { provider } = identifySaasProvider(asset);
    if (!acc[provider]) {
      acc[provider] = [];
    }
    acc[provider].push(asset);
    return acc;
  }, {});
};

/**
 * Calculate SaaS metrics for dashboard display
 *
 * @param {Array} assets - Array of all Datto assets
 * @returns {object} Metrics object with total, protected, unprotected, warning counts and byProvider map
 */
export const calculateSaasMetrics = (assets) => {
  const saasAssets = assets.filter(a => a.type?.toLowerCase() === 'saas');

  return {
    total: saasAssets.length,
    protected: saasAssets.filter(a => a.status === 'protected').length,
    unprotected: saasAssets.filter(a => a.status === 'unprotected').length,
    warning: saasAssets.filter(a => a.status === 'warning').length,
    byProvider: categorizeSaasAssets(assets),
  };
};

/**
 * Helper function to parse MCP response content
 * Handles both wrapped MCP protocol format and direct responses
 *
 * @param {*} response - MCP response (may be wrapped or direct)
 * @returns {object|null} Parsed response object or null on error
 */
export const parseMCPResponse = (response) => {
  if (!response) return null;

  // Check if response is wrapped in MCP protocol format
  if (response.content && Array.isArray(response.content)) {
    const textContent = response.content.find(c => c.type === 'text');
    if (textContent?.text) {
      try {
        return JSON.parse(textContent.text);
      } catch (e) {
        console.error('[dattoHelpers] Failed to parse MCP response:', e);
        return null;
      }
    }
  }

  // Already parsed or not in MCP format
  return response;
};
